import React, { useState, useEffect } from 'react';
import { FiUserPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { UserListItem, Partner, UserManagementState, CreateUserRequest, UpdateUserRequest } from '../../types/user-management';
import { logger } from '../../utils';
import { wso2Service } from '../../services/wso2';
import { useAuth } from '../../contexts/AuthContext';
import CreateUserModal from '../../components/admin/CreateUserModal';
import EditUserModal from '../../components/admin/EditUserModal';
import DeleteUserModal from '../../components/admin/DeleteUserModal';

const AdminDashboard: React.FC = () => {
  const { token } = useAuth();
  const [state, setState] = useState<UserManagementState>({
    users: [],
    partners: [],
    isLoading: true,
    error: null,
    selectedUser: null,
    isCreateModalOpen: false,
    isDeleteModalOpen: false,
    isEditModalOpen: false,
  });

  const fetchUsers = async () => {
    try {
      if (!token) {
        throw new Error('No access token available');
      }

      const users = await wso2Service.listUsers(token);
      setState(prev => ({
        ...prev,
        users,
        partners: users
          .filter((user: UserListItem) => user.role === 'tedy-partner')
          .map((partner: UserListItem) => ({
            id: partner.id,
            name: partner.name,
            organization: partner.organization || '',
            email: partner.email,
          })),
        isLoading: false,
      }));
    } catch (error) {
      logger.error('ADMIN', 'Failed to fetch users', { error });
      setState(prev => ({
        ...prev,
        error: 'Failed to load users',
        isLoading: false,
      }));
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const handleCreateUser = () => {
    setState(prev => ({ ...prev, isCreateModalOpen: true }));
  };

  const handleEditUser = (user: UserListItem) => {
    setState(prev => ({
      ...prev,
      selectedUser: user,
      isEditModalOpen: true,
    }));
  };

  const handleDeleteUser = (user: UserListItem) => {
    setState(prev => ({
      ...prev,
      selectedUser: user,
      isDeleteModalOpen: true,
    }));
  };

  const handleCreateSubmit = async (data: CreateUserRequest) => {
    try {
      if (!token) {
        throw new Error('No access token available');
      }

      const newUser = await wso2Service.createUser(token, data);
      setState(prev => ({
        ...prev,
        users: [...prev.users, newUser],
        partners: data.role === 'tedy-partner'
          ? [...prev.partners, {
              id: newUser.id,
              name: newUser.name,
              organization: newUser.organization || '',
              email: newUser.email,
            }]
          : prev.partners,
      }));
    } catch (error) {
      logger.error('ADMIN', 'Failed to create user', { error });
      throw error;
    }
  };

  const handleEditSubmit = async (userId: string, data: UpdateUserRequest) => {
    try {
      if (!token) {
        throw new Error('No access token available');
      }

      const updatedUser = await wso2Service.updateUser(token, userId, data);
      setState(prev => {
        const updatedUsers = prev.users.map(user => 
          user.id === userId ? updatedUser : user
        );

        const updatedPartners = prev.partners.map(partner => {
          if (partner.id === userId) {
            return {
              ...partner,
              name: updatedUser.name,
              organization: updatedUser.organization || '',
            };
          }
          return partner;
        });

        return {
          ...prev,
          users: updatedUsers,
          partners: updatedPartners,
        };
      });
    } catch (error) {
      logger.error('ADMIN', 'Failed to update user', { error });
      throw error;
    }
  };

  const handleDeleteSubmit = async (userId: string) => {
    try {
      if (!token) {
        throw new Error('No access token available');
      }

      await wso2Service.deleteUser(token, userId);
      setState(prev => ({
        ...prev,
        users: prev.users.filter(user => user.id !== userId),
        partners: prev.partners.filter(partner => partner.id !== userId),
      }));
    } catch (error) {
      logger.error('ADMIN', 'Failed to delete user', { error });
      throw error;
    }
  };

  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tedy-red"></div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 text-red-600 p-4 rounded-md">
          {state.error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-tedy-black">User Management</h1>
        <button
          onClick={handleCreateUser}
          className="btn-primary flex items-center gap-2"
        >
          <FiUserPlus /> Create User
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {state.users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {user.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.role === 'tedy-partner'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {user.role === 'tedy-partner' ? 'Partner' : 'Student'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {user.organization || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="text-tedy-red hover:text-tedy-red-dark mr-3"
                      title="Edit user"
                    >
                      <FiEdit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete user"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CreateUserModal
        isOpen={state.isCreateModalOpen}
        onClose={() => setState(prev => ({ ...prev, isCreateModalOpen: false }))}
        onSubmit={handleCreateSubmit}
        partners={state.partners}
      />

      <EditUserModal
        isOpen={state.isEditModalOpen}
        onClose={() => setState(prev => ({ ...prev, isEditModalOpen: false, selectedUser: null }))}
        onSubmit={handleEditSubmit}
        user={state.selectedUser}
        partners={state.partners}
      />

      <DeleteUserModal
        isOpen={state.isDeleteModalOpen}
        onClose={() => setState(prev => ({ ...prev, isDeleteModalOpen: false, selectedUser: null }))}
        onConfirm={handleDeleteSubmit}
        user={state.selectedUser}
      />
    </div>
  );
};

export default AdminDashboard;
